// File: components/Chat/ChatComponents.js
// All reusable UI components and utilities

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';


// Import the enhanced loading indicator
import { EnhancedLoadingIndicator } from './EnhancedLoadingIndicator';

// Simple Bouncing Dots Component
export const BouncingDots = ({ className = "" }) => (
  <div className={`flex items-center space-x-1 ${className}`}>
    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
  </div>
);

// Enhanced Loading Indicator
export const SimpleLoadingIndicator = ({ message = "DataWeb AI is thinking..." }) => (
  <EnhancedLoadingIndicator message={message} showTips={true} />
);

export const MinimalLoadingIndicator = ({ message = "DataWeb AI is thinking..." }) => (
  <EnhancedLoadingIndicator message={message} showTips={false} />
);

// ─── Capture a chart container as a PNG image ───
const captureChartElement = async (chartContainer) => {
  try {
    const rect = chartContainer.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      console.warn('⚠️ Chart container has zero dimensions');
      return null;
    }

    console.log(`📊 Capturing chart: ${Math.round(rect.width)}x${Math.round(rect.height)}`);

    const capturedCanvas = await html2canvas(chartContainer, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      // Force light mode colors for PDF
      onclone: (clonedDoc, clonedEl) => {
        clonedEl.style.backgroundColor = '#ffffff';
        clonedEl.style.color = '#111827';
        clonedEl.style.border = 'none';
        clonedEl.style.boxShadow = 'none';
        clonedEl.style.margin = '0';
        clonedEl.style.padding = '16px';

        // Fix dark mode text colors in the clone
        clonedEl.querySelectorAll('*').forEach((el) => {
          const computed = window.getComputedStyle(el);
          const color = computed.color;
          // Convert white/light text to dark for PDF
          if (color === 'rgb(255, 255, 255)' ||
              color === 'rgba(255, 255, 255, 1)' ||
              color === 'rgb(229, 231, 235)' ||
              color === 'rgb(209, 213, 219)' ||
              color === 'rgb(156, 163, 175)') {
            el.style.color = '#374151';
          }
        });

        // Ensure SVGs are visible
        clonedEl.querySelectorAll('svg').forEach((svg) => {
          svg.style.overflow = 'visible';
        });
      },
    });

    return {
      dataUrl: capturedCanvas.toDataURL('image/png', 1.0),
      width: capturedCanvas.width,
      height: capturedCanvas.height,
    };
  } catch (e) {
    console.error('❌ Failed to capture chart:', e);
    return null;
  }
};

// ─── Main Hybrid PDF Generator ───
export const generateHybridPDF = async (markdownContent, containerEl, filename = 'report.pdf') => {
  try {
    console.log('📄 Generating hybrid PDF (text + charts)...');
    console.log('📄 Container element:', containerEl ? 'provided' : 'null');
    console.log('📄 Content length:', markdownContent?.length || 0);

    const doc = new jsPDF({ format: 'a4', unit: 'mm' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const maxY = pageHeight - margin;
    let currentY = margin;

    const ensureSpace = (needed) => {
      if (currentY + needed > maxY) {
        doc.addPage();
        currentY = margin;
      }
    };

    // ── Capture ALL chart containers from the DOM ──
    const chartImages = [];
    if (containerEl) {
      // Use the data attribute we added to ChartRenderer
      const chartContainers = containerEl.querySelectorAll('[data-chart-container="true"]');
      console.log(`🔍 Found ${chartContainers.length} chart container(s) in DOM`);

      for (let i = 0; i < chartContainers.length; i++) {
        console.log(`📊 Capturing chart ${i + 1}/${chartContainers.length}...`);
        const img = await captureChartElement(chartContainers[i]);
        if (img) {
          chartImages.push(img);
          console.log(`✅ Chart ${i + 1} captured successfully`);
        } else {
          // Push null to keep index alignment with <chart_data> tags
          chartImages.push(null);
          console.warn(`⚠️ Chart ${i + 1} could not be captured`);
        }
      }
    }
    console.log(`📊 Total charts captured: ${chartImages.filter(Boolean).length}/${chartImages.length}`);

    // ── Title ──
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    const title = filename.replace('.pdf', '').replace(/_/g, ' ');
    doc.text(title, margin, currentY);
    currentY += 8;

    // ── Timestamp ──
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 4;

    // ── Divider ──
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.3);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 6;

    doc.setTextColor(55, 65, 81);

    // ── Split content by chart_data tags ──
    const parts = markdownContent.split(/(<chart_data>[\s\S]*?<\/chart_data>)/g);
    let chartIndex = 0;

    console.log(`📝 Content split into ${parts.length} parts, ${parts.filter(p => /^<chart_data>/.test(p)).length} chart tag(s)`);

    for (const part of parts) {
      const isChart = /^<chart_data>[\s\S]*<\/chart_data>$/.test(part);

      if (isChart) {
        // ── Insert chart image ──
        if (chartIndex < chartImages.length && chartImages[chartIndex]) {
          const chart = chartImages[chartIndex];
          const aspectRatio = chart.height / chart.width;
          let imgWidth = contentWidth;
          let imgHeight = contentWidth * aspectRatio;

          // Cap max height at 120mm
          if (imgHeight > 120) {
            imgHeight = 120;
            imgWidth = imgHeight / aspectRatio;
          }

          // Minimum height for readability
          if (imgHeight < 40) {
            imgHeight = 40;
            imgWidth = imgHeight / aspectRatio;
          }

          ensureSpace(imgHeight + 8);

          const xOffset = margin + (contentWidth - imgWidth) / 2;
          doc.addImage(chart.dataUrl, 'PNG', xOffset, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 6;
          console.log(`📊 Inserted chart ${chartIndex + 1} at y=${Math.round(currentY)}`);
        } else {
          // Placeholder for missing chart
          ensureSpace(15);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(156, 163, 175);
          doc.text('[Chart — view in browser for interactive version]', margin, currentY);
          currentY += 8;
          doc.setTextColor(55, 65, 81);
          doc.setFont('helvetica', 'normal');
          console.log(`⚠️ Chart ${chartIndex + 1} placeholder inserted (no image)`);
        }
        chartIndex++;
        continue;
      }

      // ── Process markdown text ──
      const textContent = part.trim();
      if (!textContent) continue;

      const lines = textContent.split('\n');

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) {
          currentY += 2;
          continue;
        }

        // ── Headings (check most specific first: #### before ###) ──
        const h4Match = trimmed.match(/^####\s+(.+)$/);
        if (h4Match) {
          ensureSpace(7);
          currentY += 1.5;
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(55, 65, 81);
          const headLines = doc.splitTextToSize(h4Match[1], contentWidth);
          headLines.forEach((l) => {
            ensureSpace(5);
            doc.text(l, margin, currentY);
            currentY += 5;
          });
          currentY += 1;
          doc.setFont('helvetica', 'normal');
          continue;
        }

        const h3Match = trimmed.match(/^###\s+(.+)$/);
        if (h3Match) {
          ensureSpace(8);
          currentY += 2;
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(31, 41, 55);
          const headLines = doc.splitTextToSize(h3Match[1], contentWidth);
          headLines.forEach((l) => {
            ensureSpace(5.5);
            doc.text(l, margin, currentY);
            currentY += 5.5;
          });
          currentY += 1.5;
          doc.setTextColor(55, 65, 81);
          continue;
        }

        const h2Match = trimmed.match(/^##\s+(.+)$/);
        if (h2Match) {
          ensureSpace(10);
          currentY += 3;
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(17, 24, 39);
          const headLines = doc.splitTextToSize(h2Match[1], contentWidth);
          headLines.forEach((l) => {
            ensureSpace(6);
            doc.text(l, margin, currentY);
            currentY += 6;
          });
          currentY += 2;
          doc.setTextColor(55, 65, 81);
          continue;
        }

        const h1Match = trimmed.match(/^#\s+(.+)$/);
        if (h1Match) {
          ensureSpace(12);
          currentY += 4;
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(17, 24, 39);
          const headLines = doc.splitTextToSize(h1Match[1], contentWidth);
          headLines.forEach((l) => {
            ensureSpace(7);
            doc.text(l, margin, currentY);
            currentY += 7;
          });
          doc.setDrawColor(209, 213, 219);
          doc.setLineWidth(0.2);
          doc.line(margin, currentY, pageWidth - margin, currentY);
          currentY += 4;
          doc.setTextColor(55, 65, 81);
          continue;
        }

        // ── Horizontal rule ──
        if (/^[-*_]{3,}$/.test(trimmed)) {
          ensureSpace(6);
          currentY += 2;
          doc.setDrawColor(209, 213, 219);
          doc.setLineWidth(0.2);
          doc.line(margin, currentY, pageWidth - margin, currentY);
          currentY += 4;
          continue;
        }

        // ── Bullet points ──
        const bulletMatch = trimmed.match(/^[-*+]\s+(.+)$/);
        if (bulletMatch) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const bulletText = bulletMatch[1]
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*(.*?)\*/g, '$1')
            .replace(/`(.*?)`/g, '$1');
          const bulletLines = doc.splitTextToSize(bulletText, contentWidth - 8);
          ensureSpace(bulletLines.length * 4.5 + 1);
          doc.text('\u2022', margin + 2, currentY);
          bulletLines.forEach((l) => {
            doc.text(l, margin + 7, currentY);
            currentY += 4.5;
          });
          currentY += 0.5;
          continue;
        }

        // ── Numbered list ──
        const numMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
        if (numMatch) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const numText = numMatch[2]
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*(.*?)\*/g, '$1');
          const numLines = doc.splitTextToSize(numText, contentWidth - 10);
          ensureSpace(numLines.length * 4.5 + 1);
          doc.text(`${numMatch[1]}.`, margin + 1, currentY);
          numLines.forEach((l) => {
            doc.text(l, margin + 9, currentY);
            currentY += 4.5;
          });
          currentY += 0.5;
          continue;
        }

        // ── Blockquote ──
        if (trimmed.startsWith('>')) {
          const quoteText = trimmed.replace(/^>\s*/, '')
            .replace(/\*\*(.*?)\*\*/g, '$1');
          doc.setFontSize(10);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(107, 114, 128);
          const quoteLines = doc.splitTextToSize(quoteText, contentWidth - 8);
          ensureSpace(quoteLines.length * 4.5 + 2);
          doc.setDrawColor(59, 130, 246);
          doc.setLineWidth(0.8);
          const quoteStartY = currentY - 2;
          quoteLines.forEach((l) => {
            doc.text(l, margin + 6, currentY);
            currentY += 4.5;
          });
          doc.line(margin + 2, quoteStartY, margin + 2, currentY - 2);
          currentY += 2;
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(55, 65, 81);
          continue;
        }

        // ── Regular paragraph ──
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(55, 65, 81);
        const cleanText = trimmed
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .replace(/`(.*?)`/g, '$1')
          .replace(/\[(.*?)\]\(.*?\)/g, '$1');
        const paraLines = doc.splitTextToSize(cleanText, contentWidth);
        paraLines.forEach((l) => {
          ensureSpace(4.5);
          doc.text(l, margin, currentY);
          currentY += 4.5;
        });
        currentY += 1.5;
      }
    }

    // ── Page numbers & footer ──
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(156, 163, 175);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 35, pageHeight - 8);
      doc.text('DataWeb AI Report', margin, pageHeight - 8);
    }

    doc.save(filename);
    console.log(`✅ Hybrid PDF generated: ${filename} (${totalPages} pages, ${chartImages.filter(Boolean).length} charts)`);
    return true;
  } catch (error) {
    console.error('❌ Error generating hybrid PDF:', error);
    return false;
  }
};

export const generatePDFFromDOM = generateHybridPDF;

export const generatePDF = async (content, filename = 'report.pdf') => {
  return generateHybridPDF(content, null, filename);
};


// ─── UPDATED PDF Download Button with DOM ref support ───
export const PDFDownloadButton = ({
  content,
  contentRef,
  filename = 'report.pdf',
  className = ''
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      await generateHybridPDF(
        content,
        contentRef?.current || null,
        filename
      );
    } catch (error) {
      console.error('❌ PDF download failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={handleDownload}
      disabled={isGenerating || !content}
      className={`p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 
                  hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors 
                  disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      title={isGenerating ? 'Generating PDF...' : 'Download as PDF'}
    >
      {isGenerating ? (
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10"
                  stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 
                   7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 
                   012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 
                   01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )}
    </button>
  );
};

// ─── Enhanced Markdown Renderer ───
export const MarkdownRenderer = ({ content }) => {
  if (!content || typeof content !== 'string') {
    console.log('No content provided to markdown renderer');
    return <div className="text-gray-500 italic">Loading content...</div>;
  }

  const cleanedContent = content
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '    ')
    .replace(/\\\*/g, '*')
    .replace(/\\#/g, '#')
    .replace(/\\\[/g, '[')
    .replace(/\\\]/g, ']')
    .trim();

  if (cleanedContent.length === 0) {
    console.log('Content is empty after cleaning');
    return <div className="text-gray-500 italic">Loading content...</div>;
  }

  return (
    <div className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:text-gray-700 dark:prose-ul:text-gray-300 prose-ol:text-gray-700 dark:prose-ol:text-gray-300">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h1: ({ children, ...props }) => (
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 border-b-2 border-gray-200 dark:border-gray-700 pb-2" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 mt-6" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 mt-4" {...props}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-3" {...props}>
              {children}
            </h4>
          ),
          ul: ({ children, ...props }) => (
            <ul className="list-disc list-outside ml-6 space-y-1 my-3 text-gray-700 dark:text-gray-300" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal list-outside ml-6 space-y-1 my-3 text-gray-700 dark:text-gray-300" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="leading-relaxed pl-1" {...props}>
              {children}
            </li>
          ),
          p: ({ children, ...props }) => (
            <p className="mb-3 leading-relaxed text-gray-700 dark:text-gray-300" {...props}>
              {children}
            </p>
          ),
          strong: ({ children, ...props }) => (
            <strong className="font-bold text-gray-900 dark:text-white" {...props}>
              {children}
            </strong>
          ),
          em: ({ children, ...props }) => (
            <em className="italic text-gray-800 dark:text-gray-200" {...props}>
              {children}
            </em>
          ),
          code: ({ inline, children, ...props }) => {
            if (inline) {
              return (
                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-gray-900 dark:text-gray-100" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto my-4">
                <code className="text-sm font-mono text-gray-900 dark:text-gray-100" {...props}>
                  {children}
                </code>
              </pre>
            );
          },
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-gray-50 dark:bg-gray-800" {...props}>
              {children}
            </thead>
          ),
          th: ({ children, ...props }) => (
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700" {...props}>
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700" {...props}>
              {children}
            </td>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 py-2 rounded-r" {...props}>
              {children}
            </blockquote>
          ),
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),
          hr: (props) => (
            <hr className="my-6 border-gray-300 dark:border-gray-600" {...props} />
          ),
        }}
      >
        {cleanedContent}
      </ReactMarkdown>
    </div>
  );
};

// ─── Chat Header Component ───
export const ChatHeader = ({
  wsConnected,
  wsConnecting,
  isChatHistoryOpen,
  isReportsPanelOpen,
  onToggleChatHistory,
  onToggleReports,
  onRestart,
  onLogout,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleMenuClick = (action) => {
    action();
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="relative bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
              DataWeb AI
            </h1>
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400' : wsConnecting ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'}`}></div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {wsConnecting ? 'Connecting' : wsConnected ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
        </div>

        <nav className="hidden md:flex items-center space-x-3">
          <button
            onClick={onToggleChatHistory}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              isChatHistoryOpen
                ? 'text-blue-700 bg-blue-50 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Chat History
          </button>

          <button
            onClick={onToggleReports}
            className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              isReportsPanelOpen
                ? 'text-green-700 bg-green-50 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-600'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Reports
          </button>

          <button
            onClick={onRestart}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </button>

          <button
            onClick={onLogout}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 dark:bg-gray-700 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </nav>

        <div className="md:hidden">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-white dark:bg-gray-800 shadow-lg rounded-b-lg border-t border-gray-200 dark:border-gray-700 z-50">
          <nav className="flex flex-col p-4 space-y-2">
            <button
              onClick={() => handleMenuClick(onToggleChatHistory)}
              className={`text-left w-full p-3 font-medium rounded-md transition-colors ${
                isChatHistoryOpen
                  ? 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              💬 Chat History
            </button>
            <button
              onClick={() => handleMenuClick(onToggleReports)}
              className={`text-left w-full p-3 font-medium rounded-md transition-colors ${
                isReportsPanelOpen
                  ? 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              📄 Reports
            </button>
            <button
              onClick={() => handleMenuClick(onRestart)}
              className="text-left w-full p-3 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              ✨ New Chat
            </button>
            <button
              onClick={() => handleMenuClick(onLogout)}
              className="text-left w-full p-3 font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
            >
              🚪 Logout
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

// ─── Chat Input Component ───
export const ChatInput = ({
  message,
  setMessage,
  onSendMessage,
  wsConnected,
  wsConnecting,
  isLoading,
  isTyping,
  textareaRef
}) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && wsConnected) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = () => {
    if (!message.trim() || !wsConnected) return;
    const messageToSend = message.trim();
    setMessage('');
    onSendMessage(messageToSend);
  };

  const getPlaceholder = () => {
    return "Ask me anything about business, markets, or research...";
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end space-x-4">
          <div className="flex-1">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!wsConnected || isLoading || isTyping}
              placeholder={getPlaceholder()}
              rows={1}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-500 transition-all duration-200"
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
          </div>

          <button
            onClick={handleSendMessage}
            disabled={!message.trim() || !wsConnected || isLoading || isTyping}
            className={`p-3 rounded-xl transition-all duration-200 ${
              !message.trim() || !wsConnected || isLoading || isTyping
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105 shadow-lg hover:shadow-xl'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};